import { Injectable } from '@angular/core';

import { RouteTag } from '@frontend/sports/common/core/data-access/route';
import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, distinctUntilChanged, filter, map } from 'rxjs';

import { PopupManager } from '../../../lib/popup/popup-manager.service';
import { IBetslipRootState } from '../../base/store/state';
import { BetslipType } from '../../core/betslip-type';
import { BetslipIntegrationService } from '../../integration/betslip-integration.service';
import { betslipTypeStateSelector } from '../../modules/types/selectors';
import { BetslipReturnsMessagesAccaBoostService } from './betslip-returns-messages-acca-boost.service';
import { RemindMeLaterService } from './remind-me-later.service';

@Injectable()
export class BetslipReturnsMessagesVisibilityService {
    constructor(
        private routerEventsService: RouterEventsService,
        private popupManager: PopupManager,
        private store: Store<IBetslipRootState>,
        private returnsMessagesAccaBoostService: BetslipReturnsMessagesAccaBoostService,
        private integrationService: BetslipIntegrationService,
        private remaindMeLaterService: RemindMeLaterService,
    ) {}

    isVisible$(): Observable<boolean> {
        const isBetslipOpen = () => this.popupManager.isOpen() && this.popupManager.isPopupOpen('betslip');

        const isRestrictedLocation$ = this.routerEventsService.currentActivationEnd.pipe(
            map((activationEnd) => activationEnd?.snapshot.data['tag'] === RouteTag.MyBets),
            distinctUntilChanged(),
        );

        const shouldShowReturnsMessages$ = this.integrationService.shouldShowBetslipReturnsMessage$();

        const isInEmb$ = this.store
            .select(betslipTypeStateSelector)
            .pipe(map((typeState) => typeState.base.currentSelectedType === BetslipType.EditBet));

        const hasAccaBoost$ = this.returnsMessagesAccaBoostService.accaBoostNotifications$.pipe(map((notification) => !!notification));
        const hasRemindMeLaterNotification$ = this.remaindMeLaterService.remindMeNotification$.pipe(map((notification) => !!notification));

        return combineLatest([shouldShowReturnsMessages$, isInEmb$, hasAccaBoost$, isRestrictedLocation$, hasRemindMeLaterNotification$]).pipe(
            map(([shouldShowReturnMessages, isInEmb, hasAccaBoost, isRestrictedLocation, hasRemindMeLaterNotification]) => {
                const returnsMessagesAllowed = shouldShowReturnMessages || hasAccaBoost || hasRemindMeLaterNotification;

                return returnsMessagesAllowed && !isRestrictedLocation && !isInEmb && !isBetslipOpen();
            }),
            distinctUntilChanged(),
        );
    }

    hideOnPopupOpen$(): Observable<boolean> {
        return this.routerEventsService.currentActivationEnd.pipe(
            map(() => this.popupManager.isOpen()),
            filter((isOpen) => isOpen),
        );
    }
}
